// The Ready design gallery in "Мій сайт": how the API's readyDesigns list is grouped and shown,
// the same rules the mobile app follows (apps/app/lib/readyDesigns.ts).

export const UNIVERSAL_DESIGN_GROUP = "universal";

// The Design library's fixed group order, as apps/sites/library/groups.json has it.
// Names: sitePage.designs.groups.<id>.
export const READY_DESIGN_GROUP_IDS = [
  "photo-video",
  "hosts-shows",
  "music",
  "decor-floristry",
  "food-sweets",
  "venues",
  "wedding-fashion",
  "beauty",
  "tech-effects",
  "ceremony-planning",
  "transport",
  "printing",
] as const;

// On "Усі" every group shows one row of this many designs (the app shows fewer of the groups
// that are not the Provider's; a wide screen fits 6 in a row); "Усі N ›" opens the whole group.
export const GROUP_PREVIEW_COUNT = 6;

export const ALL_DESIGNS_FILTER = "all";

// How many designs the page without a site shows above "Обрати готовий дизайн".
export const TEASER_DESIGN_COUNT = 3;

export interface ReadyDesignLike {
  id: string;
  group: string;
}

export interface ReadyDesignGroupSection<T extends ReadyDesignLike> {
  group: string;
  own: boolean;
  total: number;
  designs: T[];
}

// The Provider's own groups first, then the universal designs, then every other group, each part
// in the library's order. A group the admin does not know yet goes last.
export function orderDesignGroups(
  ownGroups: readonly string[],
  designs: readonly ReadyDesignLike[]
): string[] {
  const present = new Set(designs.map((design) => design.group));
  const libraryOrder: string[] = [...READY_DESIGN_GROUP_IDS];
  const own = libraryOrder.filter((group) => ownGroups.includes(group));
  const known = [
    ...own,
    UNIVERSAL_DESIGN_GROUP,
    ...libraryOrder.filter((group) => !own.includes(group)),
  ];
  const unknown = Array.from(present).filter((group) => !known.includes(group));
  return [...known, ...unknown].filter((group) => present.has(group));
}

export function isOwnSectionGroup(group: string, ownGroups: readonly string[]) {
  return group === UNIVERSAL_DESIGN_GROUP || ownGroups.includes(group);
}

// The chips over the gallery: "Усі" and each group with its count, in gallery order.
export function getDesignChips(ownGroups: readonly string[], designs: readonly ReadyDesignLike[]) {
  return [
    { id: ALL_DESIGNS_FILTER, count: designs.length },
    ...orderDesignGroups(ownGroups, designs).map((group) => ({
      id: group,
      count: designs.filter((design) => design.group === group).length,
    })),
  ];
}

// The gallery's sections for a filter: on "Усі" every group cut to its preview count, with the
// own and universal groups before the "Інші групи" divider; one group shows whole.
export function getDesignSections<T extends ReadyDesignLike>(
  ownGroups: readonly string[],
  designs: readonly T[],
  filter: string
): { leading: ReadyDesignGroupSection<T>[]; others: ReadyDesignGroupSection<T>[] } {
  const sections = orderDesignGroups(ownGroups, designs)
    .filter((group) => filter === ALL_DESIGNS_FILTER || group === filter)
    .map((group) => {
      const inGroup = designs.filter((design) => design.group === group);
      const own = isOwnSectionGroup(group, ownGroups);
      const limit = filter !== ALL_DESIGNS_FILTER ? inGroup.length : GROUP_PREVIEW_COUNT;
      return { group, own, total: inGroup.length, designs: inGroup.slice(0, limit) };
    });
  if (filter !== ALL_DESIGNS_FILTER) {
    return { leading: sections, others: [] };
  }
  return {
    leading: sections.filter((section) => section.own),
    others: sections.filter((section) => !section.own),
  };
}

interface SiteForReadyDesign {
  publishedVersion?: number | null;
  hasUnpublishedChanges: boolean;
  versions: { number: number; readyDesign?: string | null }[];
}

// "Поточний": the Ready design the site on the web started from. A design applied to the draft
// becomes current only once it is published.
export function getCurrentReadyDesign(site: SiteForReadyDesign | null | undefined): string | null {
  if (!site || site.publishedVersion == null) return null;
  return (
    site.versions.find((version) => version.number === site.publishedVersion)?.readyDesign ?? null
  );
}

// Applying replaces the draft, so the Provider confirms first when the draft holds changes that are
// not on the site yet. The first site, or a draft equal to the site, needs no confirmation.
export function needsDraftReplaceConfirmation(
  site: Pick<SiteForReadyDesign, "hasUnpublishedChanges"> | null | undefined
) {
  return site?.hasUnpublishedChanges === true;
}

// A design's proper name; a design no longer in the library keeps its id.
export function getReadyDesignName(
  id: string,
  designs: readonly { id: string; name: string }[] | null | undefined
) {
  return designs?.find((design) => design.id === id)?.name ?? id;
}

// A group's name from the dictionary, or its id when the admin does not know the group yet.
export function getDesignGroupName(group: string, names: Record<string, string>) {
  return names[group] ?? group;
}

// The group line on a design's page: universal designs say so, others name their group.
export function getDesignGroupLabel(
  group: string,
  names: Record<string, string>,
  universalDesign: string
) {
  return group === UNIVERSAL_DESIGN_GROUP ? universalDesign : getDesignGroupName(group, names);
}
